import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Lock, Plus, ShieldCheck, Trash2, Users } from 'lucide-react';
import { clsx } from 'clsx';

import { api } from '../lib/api.ts';
import { useAuth } from '../contexto/auth.tsx';
import type { Nivel } from '../contexto/auth.tsx';
import { Aviso, Botao, Campo, Carregando, Etiqueta } from '../componentes/ui.tsx';

interface Modulo {
  codigo: string;
  nome: string;
  descricao: string;
  niveis: { nivel: Nivel; descricao: string }[];
}

interface Perfil {
  codigo: string;
  nome: string;
  descricao: string | null;
  sistema: boolean;
  usuarios: number;
  permissoes: Record<string, Nivel>;
}

interface Resposta {
  modulos: Modulo[];
  perfis: Perfil[];
}

const ROTULO: Record<Nivel, string> = {
  visualizar: 'Visualização',
  editar: 'Edição',
  excluir: 'Exclusão',
};

/**
 * Perfis e o que cada um pode.
 *
 * A matriz é uma escolha única por módulo, e não três caixas de marcar, porque
 * os níveis são cumulativos: marcar "exclusão" sem "visualização" seria uma
 * permissão que ninguém consegue exercer. Cada linha é uma escolha entre
 * "sem acesso" e o nível mais alto que aquele perfil tem ali.
 */
export function Permissoes() {
  const { usuario } = useAuth();
  const cliente = useQueryClient();
  const [selecionado, setSelecionado] = useState<string | null>(null);
  const [rascunho, setRascunho] = useState<Record<string, Nivel | null>>({});
  const [criando, setCriando] = useState(false);

  const consulta = useQuery({
    queryKey: ['perfis'],
    queryFn: () => api<Resposta>('/perfis'),
  });

  const perfis = consulta.data?.perfis ?? [];
  const modulos = consulta.data?.modulos ?? [];

  const atual = useMemo(
    () => perfis.find((p) => p.codigo === selecionado) ?? perfis[0] ?? null,
    [perfis, selecionado],
  );

  // Ao trocar de perfil, o rascunho passa a ser o que está gravado.
  useEffect(() => {
    if (atual === null) return;
    setRascunho({ ...atual.permissoes });
  }, [atual?.codigo, consulta.dataUpdatedAt]);

  const salvar = useMutation({
    mutationFn: (codigo: string) =>
      api<{ ok: true }>(`/perfis/${codigo}/permissoes`, {
        metodo: 'PUT',
        corpo: { permissoes: rascunho },
      }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['perfis'] }),
  });

  const apagar = useMutation({
    mutationFn: (codigo: string) => api<{ ok: true }>(`/perfis/${codigo}`, { metodo: 'DELETE' }),
    onSuccess: () => {
      setSelecionado(null);
      cliente.invalidateQueries({ queryKey: ['perfis'] });
    },
  });

  if (consulta.isLoading) return <Carregando texto="Carregando perfis…" />;
  if (consulta.isError) {
    return (
      <Aviso tom="erro" titulo="Não foi possível carregar os perfis">
        <p>{(consulta.error as Error).message}</p>
      </Aviso>
    );
  }

  const mudou =
    atual !== null &&
    modulos.some((m) => (rascunho[m.codigo] ?? null) !== (atual.permissoes[m.codigo] ?? null));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
            <ShieldCheck className="size-6 text-slate-400" />
            Perfis e permissões
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Cada perfil recebe um nível por módulo. Os níveis são cumulativos: quem exclui também
            edita, e quem edita também visualiza.
          </p>
        </div>
        <Botao tipo="secundario" onClick={() => setCriando((c) => !c)}>
          <Plus className="size-4" />
          Novo perfil
        </Botao>
      </div>

      {criando && <NovoPerfil aoCriar={() => setCriando(false)} />}

      <div className="grid gap-6 lg:grid-cols-[16rem_1fr]">
        <aside className="space-y-1">
          {perfis.map((p) => (
            <button
              key={p.codigo}
              onClick={() => setSelecionado(p.codigo)}
              className={clsx(
                'w-full rounded border px-3 py-2 text-left transition',
                atual?.codigo === p.codigo
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-200 hover:bg-slate-50',
              )}
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                {p.sistema && <Lock className="size-3.5 shrink-0" />}
                {p.nome}
              </span>
              <span
                className={clsx(
                  'mt-0.5 flex items-center gap-1 text-xs',
                  atual?.codigo === p.codigo ? 'text-slate-300' : 'text-slate-500',
                )}
              >
                <Users className="size-3" />
                {p.usuarios} {p.usuarios === 1 ? 'usuário' : 'usuários'}
              </span>
            </button>
          ))}
        </aside>

        {atual !== null && (
          <section className="space-y-4">
            <div className="rounded border border-slate-200 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-slate-900">{atual.nome}</p>
                  <p className="mt-0.5 font-mono text-xs text-slate-500">{atual.codigo}</p>
                  {atual.descricao !== null && (
                    <p className="mt-2 max-w-prose text-sm text-slate-600">{atual.descricao}</p>
                  )}
                </div>
                {!atual.sistema && (
                  <Botao
                    tipo="perigo"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Apagar o perfil ${atual.nome}? Isso não pode ser desfeito.`,
                        )
                      ) {
                        apagar.mutate(atual.codigo);
                      }
                    }}
                    carregando={apagar.isPending}
                  >
                    <Trash2 className="size-4" />
                    Apagar perfil
                  </Botao>
                )}
              </div>
            </div>

            {atual.sistema ? (
              <Aviso titulo="O administrador não é limitável">
                <p>
                  O perfil de administrador tem acesso irrestrito por definição. Se desse para tirar
                  permissões dele, um administrador poderia trancar a si mesmo para fora do sistema
                  — e a única saída seria mexer direto no banco de dados.
                </p>
                <p className="mt-2">
                  Administrar perfis e permissões também é exclusivo dele: um perfil que pudesse
                  editar esta tela se promoveria a administrador sozinho.
                </p>
              </Aviso>
            ) : (
              <>
                {apagar.isError && (
                  <Aviso tom="erro">
                    <p>{(apagar.error as Error).message}</p>
                  </Aviso>
                )}
                {salvar.isError && (
                  <Aviso tom="erro">
                    <p>{(salvar.error as Error).message}</p>
                  </Aviso>
                )}

                <div className="space-y-3">
                  {modulos.map((m) => (
                    <LinhaDoModulo
                      key={m.codigo}
                      modulo={m}
                      nivel={rascunho[m.codigo] ?? null}
                      onChange={(n) => setRascunho((r) => ({ ...r, [m.codigo]: n }))}
                    />
                  ))}
                </div>

                <div className="flex items-center gap-3">
                  <Botao
                    onClick={() => salvar.mutate(atual.codigo)}
                    carregando={salvar.isPending}
                    desabilitado={!mudou}
                  >
                    Salvar permissões
                  </Botao>
                  {mudou ? (
                    <span className="text-sm text-amber-700">Há alterações não salvas.</span>
                  ) : (
                    salvar.isSuccess && (
                      <span className="inline-flex items-center gap-1 text-sm text-emerald-700">
                        <Check className="size-4" />
                        Salvo. Vale já na próxima ação de quem usa este perfil.
                      </span>
                    )
                  )}
                </div>

                {atual.codigo === usuario?.perfil && (
                  <Aviso tom="erro" titulo="Este é o seu próprio perfil">
                    <p>
                      Tirar uma permissão aqui tira o seu acesso na hora, sem precisar sair e entrar
                      de novo.
                    </p>
                  </Aviso>
                )}
              </>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

function LinhaDoModulo({
  modulo,
  nivel,
  onChange,
}: {
  modulo: Modulo;
  nivel: Nivel | null;
  onChange: (n: Nivel | null) => void;
}) {
  const opcoes: { valor: Nivel | null; rotulo: string; dica: string }[] = [
    { valor: null, rotulo: 'Sem acesso', dica: 'A tela nem aparece no menu.' },
    ...modulo.niveis.map((n) => ({
      valor: n.nivel as Nivel | null,
      rotulo: ROTULO[n.nivel],
      dica: n.descricao,
    })),
  ];

  return (
    <div className="rounded border border-slate-200 p-4">
      <div className="mb-3">
        <p className="font-medium text-slate-900">{modulo.nome}</p>
        <p className="text-xs text-slate-500">{modulo.descricao}</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {opcoes.map((o) => {
          const ativo = nivel === o.valor;
          return (
            <button
              key={o.rotulo}
              onClick={() => onChange(o.valor)}
              title={o.dica}
              className={clsx(
                'rounded border px-3 py-1.5 text-left text-sm transition',
                ativo
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-200 text-slate-600 hover:border-slate-400',
              )}
            >
              <span className="block font-medium">{o.rotulo}</span>
              <span className={clsx('block text-xs', ativo ? 'text-slate-300' : 'text-slate-500')}>
                {o.dica}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NovoPerfil({ aoCriar }: { aoCriar: () => void }) {
  const cliente = useQueryClient();
  const [nome, setNome] = useState('');
  const [codigo, setCodigo] = useState('');
  const [descricao, setDescricao] = useState('');

  const criar = useMutation({
    mutationFn: () =>
      api<{ codigo: string }>('/perfis', { metodo: 'POST', corpo: { codigo, nome, descricao } }),
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: ['perfis'] });
      setNome('');
      setCodigo('');
      setDescricao('');
      aoCriar();
    },
  });

  // O código é o que fica gravado em cada usuário e no log; sugerir a partir
  // do nome evita que alguém invente um identificador com acento e espaço.
  function sugerirCodigo(v: string) {
    setNome(v);
    setCodigo(
      v
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 32),
    );
  }

  return (
    <div className="space-y-4 rounded border border-slate-200 bg-slate-50 p-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <Campo rotulo="Nome" valor={nome} onChange={sugerirCodigo} />
        <Campo
          rotulo="Código"
          valor={codigo}
          onChange={setCodigo}
          dica="Identificador interno. Aparece no log e não muda depois."
        />
        <Campo rotulo="Descrição" valor={descricao} onChange={setDescricao} />
      </div>

      {criar.isError && (
        <Aviso tom="erro">
          <p>{(criar.error as Error).message}</p>
        </Aviso>
      )}

      <div className="flex items-center gap-3">
        <Botao onClick={() => criar.mutate()} carregando={criar.isPending}>
          Criar perfil
        </Botao>
        <Etiqueta tom="neutro">nasce sem nenhuma permissão</Etiqueta>
      </div>
    </div>
  );
}
